import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  Inject,
  ViewChild,
  type OnDestroy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CurrentScopeFacade,
  GlobalStoreService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { Brand, Chat } from '@principle-theorem/principle-core';
import { IChat } from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  doc,
  doc$,
  filterUndefined,
} from '@principle-theorem/shared';
import { Observable, Subject, combineLatest } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ChatActions } from '../../chat-actions';
import { ChatMessagesBloc } from '../../chat-messages-bloc';
import { IChatSummary, getChatSummary$ } from '../../chats-dashboard.store';

@Component({
  selector: 'pr-floating-chat',
  templateUrl: './floating-chat.component.html',
  styleUrl: './floating-chat.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FloatingChatComponent implements OnDestroy, AfterViewInit {
  private _onDestroy$ = new Subject<void>();
  summary$: Observable<IChatSummary>;
  actions: ChatActions;
  bloc: ChatMessagesBloc;
  @ViewChild('chatContainer') scrollContainer: ElementRef<HTMLElement>;

  @HostBinding('class.print:!hidden')
  readonly printHidden = true;

  constructor(
    private _currentScope: CurrentScopeFacade,
    private _organisation: OrganisationService,
    private _snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) chatRef: DocumentReference<IChat>,
    public dialogRef: MatDialogRef<FloatingChatComponent>,
    private _globalStore: GlobalStoreService
  ) {
    const staffer$ = this._organisation.staffer$.pipe(filterUndefined());

    this.summary$ = combineLatest([
      this._currentScope.currentBrand$.pipe(
        filterUndefined(),
        switchMap((brand) => doc$(doc(Brand.chatCol(brand), chatRef.id)))
      ),
      staffer$,
    ]).pipe(
      switchMap(([chat, staffer]) =>
        getChatSummary$(chat, staffer, this._globalStore)
      ),
      filterUndefined()
    );

    this.actions = new ChatActions(
      this.summary$,
      staffer$,
      this._snackBar,
      this._onDestroy$
    );

    this.bloc = new ChatMessagesBloc(this.actions, staffer$, this._onDestroy$);

    combineLatest([this.summary$, staffer$])
      .pipe(takeUntil(this._onDestroy$))
      .subscribe(([summary, staffer]) => {
        if (Chat.userHasAccess(summary.chat, staffer.ref)) {
          return;
        }

        this.dialogRef.close();
      });
  }

  ngAfterViewInit(): void {
    this.bloc.scrollContainer = this.scrollContainer;
    this.bloc.loadMoreOnScroll();
    this.bloc.clearNewMessagesOnScroll();
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
